import type { CallScript } from "@frontierx/shared/schemas/CallScript";
import { CheckCircleIcon, Lightbulb, MessageCircle } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { SmoothScrollArea } from "@/components/ui/smooth-scroll-area";
import { useAnalytics } from "../Analytics/AnalyticsProvider";
import { useOrganization } from "../Auth/UnifiedAuthProvider";

import { useCallContext } from "../Call/CallContext";
import { PanelHeader } from "../PanelHeader";
import { CallScriptSelector } from "./CallScriptSelector";
import { PlaybookScriptItemNextPrev } from "./PlaybookScriptItemNextPrev";

declare const __API_URL__: string;

export interface SmoothScrollAreaElement extends HTMLDivElement {
  smoothScrollTo: (options?: ScrollToOptions) => void;
}

export function PlaybookScriptDisplay() {
  const {
    mainSocket: _mainSocket,
    roomId,
    handleCompleteScriptItem,
    handleSetCurrentScriptItem,
    playbook,
    currentScriptItem,
  } = useCallContext();

  const scrollAreaRef = useRef<SmoothScrollAreaElement>(null);
  const currentItemRef = useRef<HTMLDivElement>(null);

  const scrollToCurrentItem = useCallback(() => {
    if (currentItemRef.current && scrollAreaRef.current) {
      const scrollArea = scrollAreaRef.current;
      const currentItem = currentItemRef.current;

      const scrollAreaHeight = scrollArea.clientHeight;
      const itemTop = currentItem.offsetTop;
      const itemHeight = currentItem.clientHeight;

      // Calculate the scroll position to center the current item
      const scrollPosition = itemTop - scrollAreaHeight / 2 + itemHeight / 2;

      scrollArea.smoothScrollTo({ top: scrollPosition });
    }
  }, []);

  useEffect(() => {
    // Use a small timeout to ensure the DOM has updated
    const timeoutId = setTimeout(scrollToCurrentItem, 0);

    return () => clearTimeout(timeoutId);
  }, [scrollToCurrentItem]);

  useEffect(() => {
    if (!playbook) return;
    if (
      !currentScriptItem &&
      playbook.sections.length > 0 &&
      playbook.sections[0].scriptItems.length > 0
    ) {
      handleSetCurrentScriptItem(playbook.sections[0].scriptItems[0]);
    }
  }, [playbook, currentScriptItem, handleSetCurrentScriptItem]);

  const handlePrevious = useCallback(() => {
    if (!playbook) return;
    const allScriptItems = playbook.sections.flatMap(
      (section) => section.scriptItems,
    );
    const currentIndex = allScriptItems.findIndex(
      (item) => item.id === currentScriptItem?.id,
    );
    if (currentIndex > 0) {
      handleSetCurrentScriptItem(allScriptItems[currentIndex - 1]);
    }
  }, [playbook, currentScriptItem, handleSetCurrentScriptItem]);

  const handleNext = useCallback(() => {
    if (!playbook) return;
    const allScriptItems = playbook.sections.flatMap(
      (section) => section.scriptItems,
    );
    const currentIndex = allScriptItems.findIndex(
      (item) => item.id === currentScriptItem?.id,
    );
    if (currentIndex < allScriptItems.length - 1) {
      handleSetCurrentScriptItem(allScriptItems[currentIndex + 1]);
    }
  }, [playbook, currentScriptItem, handleSetCurrentScriptItem]);

  const currentSection = playbook?.sections?.find(
    (section) => section.id === playbook.currentSectionId,
  );

  const [selectedScript, setSelectedScript] = useState<CallScript | null>(null);
  const { organization: _organization } = useOrganization();
  const { trackEvent } = useAnalytics();

  const handleSwapScript = useCallback(
    async (callScript: CallScript) => {
      try {
        console.log("[PlaybookScriptDisplay] Swapping script:", callScript);

        // Make direct HTTP POST to CallServer Durable Object
        const response = await fetch(
          `${__API_URL__}/parties/call/${roomId}/api/swap-script`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              type: "SWAP_SCRIPT",
              payload: { scriptId: callScript.id },
            }),
          },
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to swap script: ${errorText}`);
        }

        // Update UI only after successful response
        setSelectedScript(callScript);
        console.log(
          "[PlaybookScriptDisplay] Script swapped successfully via HTTP POST to CallServer",
        );
        trackEvent("script_swapped_during_call", { scriptId: callScript.id });
      } catch (error) {
        console.error("Error swapping script:", error);
      }
    },
    [roomId, trackEvent],
  );
  const hasPlaybook = playbook?.sections && playbook.sections.length > 0;
  const getHeaderTitle = () => {
    if (!hasPlaybook) return "Select a script to begin";
    if (!currentSection) return "Loading…";
    return currentSection.name;
  };
  const headerTitle = getHeaderTitle();

  return (
    <div className="flex h-full flex-col">
      <PanelHeader title={headerTitle}>
        <div className="flex w-full flex-row space-x-2">
          {hasPlaybook && (
            <div className="pt-1">
              <PlaybookScriptItemNextPrev
                onPrevious={handlePrevious}
                onNext={handleNext}
                onLocate={scrollToCurrentItem}
              />
            </div>
          )}
          <div>
            <CallScriptSelector
              size="sm"
              label={hasPlaybook ? "Switch script" : "Select script"}
              selectedScript={selectedScript}
              onSelect={(callScript: CallScript) =>
                handleSwapScript(callScript)
              }
            />
          </div>
        </div>
      </PanelHeader>
      <div className="flex flex-grow flex-col overflow-hidden border-b border-gray-200">
        <SmoothScrollArea ref={scrollAreaRef} className="h-full" smoothScroll>
          <div>
            {!hasPlaybook ? (
              <div className="flex h-full items-center justify-center p-8">
                <div className="text-center space-y-4">
                  <div className="text-gray-500 text-lg">
                    No script selected for this call
                  </div>
                  <div className="text-gray-400 text-sm max-w-md">
                    Select a script using the "Select script" button above to
                    begin guided assistance for this call.
                  </div>
                </div>
              </div>
            ) : (
              playbook?.sections?.flatMap((section) =>
                section.scriptItems.map((item) => (
                  <div
                    key={item.id}
                    ref={
                      item.id === currentScriptItem?.id ? currentItemRef : null
                    }
                    className={`border-b border-b-gray-100 p-2 ${
                      item.completed ? "text-green-500" : "text-gray-900"
                    } ${
                      item.id === currentScriptItem?.id
                        ? "bg-yellow-100"
                        : "hover:bg-yellow-50"
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <div className="flex-shrink-0">
                        <Button
                          variant={"ghost"}
                          className="px-2"
                          onClick={() => {
                            handleCompleteScriptItem(item);
                          }}
                        >
                          <ScriptItemIcon item={item} />
                        </Button>
                      </div>
                      <div className="pr-4">
                        <div className="font-medium">
                          <button
                            type="button"
                            onClick={() => handleSetCurrentScriptItem(item)}
                            className="inline text-left text-gray-700 hover:text-black"
                          >
                            {item.text}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )),
              )
            )}
          </div>
        </SmoothScrollArea>
      </div>
    </div>
  );
}

const ScriptItemIcon = ({
  item,
}: {
  item: { completed: boolean; variant?: string };
}) => {
  if (item.completed) return <CheckCircleIcon size={16} />;
  if (item.variant === "drill_down") return <Lightbulb size={16} />;
  return <MessageCircle size={16} />;
};
